import { GET_FHIR_TASK } from "adapters/ehrbridge.service";
import HTTPRequest from "adapters/http";
import { getTenantFromToken } from "config/helpers/common_helper";
import { store } from "redux/store";
import { generate_notification } from "redux/actions/AppActions";
import generateLabel from "config/helpers/syncLabel";

const STORAGE_KEY = "global_sync_active_tasks";
const POLL_INTERVAL = 10000;

// Stale task detection constants
const TWELVE_HOURS_MS = 12 * 60 * 60 * 1000;
const PRIORITY_MAX_DURATION_MS = 15 * 60 * 1000; // 15 minutes
const FULL_MAX_DURATION_MS = 2 * 60 * 60 * 1000; // 2 hours

// In-memory state
const activeTasks = new Set();
const taskDataCache = new Map();
const listeners = new Map(); // taskId -> Set of callbacks
const lastProgressMap = new Map();

let pollerTimer = null;

// Helper function to check if a task is stale
function isTaskStale(resource) {
  if (!resource) return false;

  const outputStr = resource?.output?.[0]?.valueString;
  if (!outputStr) return false;

  try {
    const data = JSON.parse(outputStr);
    const { startedAt, updatedAt, endedAt } = data;

    // If task is completed (has endedAt), it's not stale
    if (endedAt) return false;

    // If no startedAt or updatedAt, cannot determine staleness
    if (!startedAt && !updatedAt) return false;

    const currentTime = Date.now();
    const taskTimestamp = updatedAt || startedAt;

    // Check if task is older than 12 hours
    if (currentTime - taskTimestamp < TWELVE_HOURS_MS) return false;

    // Extract sync strategy from task meta tags
    const syncStrategy = resource?.meta?.tag?.find(
      (tag) =>
        tag?.system === "http://enliv.com/fhir/coding/task/tag/sync-strategy"
    )?.code;

    // Determine max duration based on sync strategy
    const maxDuration =
      syncStrategy === "PRIORITY"
        ? PRIORITY_MAX_DURATION_MS
        : syncStrategy === "FULL"
        ? FULL_MAX_DURATION_MS
        : null;

    if (!maxDuration) return false;

    // Check if task has exceeded max duration
    const taskDuration = currentTime - startedAt;
    return taskDuration > maxDuration;
  } catch (e) {
    console.warn("globalSyncPoller: failed to parse output for stale check", e);
    return false;
  }
}

function persistActiveTasks() {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(activeTasks)));
  } catch (e) {
    console.warn("globalSyncPoller: failed to persist active tasks", e);
  }
}

function loadPersistedTasks() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const arr = JSON.parse(raw);
    if (Array.isArray(arr)) {
      // Validate persisted tasks asynchronously
      validatePersistedTasks(arr);
    }
  } catch (e) {
    console.warn("globalSyncPoller: failed to load persisted tasks", e);
  }
}

// Async function to validate persisted tasks and filter out stale ones
async function validatePersistedTasks(taskIds) {
  const validTaskIds = [];

  try {
    const tenant = getTenantFromToken();
    if (!tenant) return;

    // Fetch all tasks from the API to validate them
    await Promise.all(
      taskIds.map(async (taskId) => {
        try {
          const fhir = await GET_FHIR_TASK(taskId);
          const resource = fhir?.entry?.[0]?.resource;

          if (resource && !isTaskStale(resource)) {
            // Task is valid and not stale, add it to active tasks
            activeTasks.add(taskId);
            validTaskIds.push(taskId);

            // Cache the task data
            taskDataCache.set(taskId, {
              fetchedAt: Date.now(),
              raw: fhir,
            });
          } else if (resource && isTaskStale(resource)) {
            console.log(`globalSyncPoller: filtering out stale task ${taskId}`);
            // Don't add stale tasks to activeTasks
          }
        } catch (e) {
          console.warn(
            `globalSyncPoller: failed to validate persisted task ${taskId}`,
            e
          );
          // Remove invalid tasks that can't be fetched
        }
      })
    );

    // Update localStorage with only valid tasks
    if (validTaskIds.length !== taskIds.length) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(validTaskIds));
    }
  } catch (e) {
    console.warn("globalSyncPoller: failed to validate persisted tasks", e);
  }
}

// Helper to extract task group label from parsed output
function extractTaskGroupLabel(parsed) {
  if (!parsed) return null;
  const groupCodePick =
    parsed.currentTaskGroupCode ||
    (Array.isArray(parsed.taskGroups) && parsed.taskGroups[0]?.code) ||
    null;
  return groupCodePick ? generateLabel(groupCodePick) : null;
}

async function pollOnce() {
  if (activeTasks.size === 0) return;
  const ids = Array.from(activeTasks);
  await Promise.all(
    ids.map(async (taskId) => {
      let shouldRemove = false;
      try {
        const notifId = `${taskId}-sync`;
        const fhir = await GET_FHIR_TASK(taskId);

        // Check if task has become stale during polling
        const resource = fhir?.entry?.[0]?.resource;
        if (resource && isTaskStale(resource)) {
          console.log(
            `globalSyncPoller: removing stale task during polling ${taskId}`
          );
          shouldRemove = true;
          // Don't process stale tasks further, but let the cleanup happen at the end
        } else {
          taskDataCache.set(taskId, { fetchedAt: Date.now(), raw: fhir });
        }
        if (!shouldRemove) {
          try {
            const entry = fhir?.entry?.[0];
            const resource = entry?.resource;
            const outputStr = resource?.output?.[0]?.valueString;
            if (outputStr) {
              const data = JSON.parse(outputStr);
              const status = data.status || resource?.status || "RUNNING";
              const progress =
                typeof data.progress === "number" ? data.progress : 0;

              // persist parsed payload and any detailed progressStats for consumers
              const existing = taskDataCache.get(taskId) || {
                fetchedAt: Date.now(),
                raw: fhir,
              };
              taskDataCache.set(taskId, {
                ...existing,
                fetchedAt: Date.now(),
                raw: fhir,
                parsed: data,
                progressStats: data.progressStats || null,
              });

              const last = lastProgressMap.get(taskId) || -1;
              // update if changed by >=1% or completed
              if (
                progress !== last &&
                (Math.abs(progress - last) >= 1 || progress === 100)
              ) {
                const taskGroupLabel = extractTaskGroupLabel(data);
                store.dispatch(
                  generate_notification({
                    id: notifId,
                    message: `Sync progress: ${progress}%`,
                    progress: progress,
                    status,
                    label: taskGroupLabel,
                    addedOn: Date.now(),
                    isRead: false,
                    type: "sync-progress",
                  })
                );
                lastProgressMap.set(taskId, progress);
              }

              // final notifications - update same notification on completion
              if (status === "COMPLETED" || progress === 100) {
                // prefer authoritative lastUpdated from the FHIR Task resource meta
                const metaLastUpdated = resource?.meta?.lastUpdated;
                const addedOn = metaLastUpdated
                  ? Date.parse(metaLastUpdated)
                  : typeof data.updatedAt === "number"
                  ? data.updatedAt
                  : Date.now();
                const taskGroupLabel = extractTaskGroupLabel(data);
                store.dispatch(
                  generate_notification({
                    id: notifId,
                    message: `Data Sync Completed.`,
                    progress: 100,
                    status: "COMPLETED",
                    label: taskGroupLabel,
                    addedOn: addedOn,
                    isRead: false,
                    type: "sync-complete",
                  })
                );
                // cleanup
                shouldRemove = true;
              }
              // If status is FAILED, don't emit a failure notification per user preference.
              if (status === "FAILED") {
                // still cleanup polling for the task
                shouldRemove = true;
              }
            }
          } catch (e) {
            // parsing errors are non-fatal for polling; log and continue
            console.warn(
              "globalSyncPoller: failed to parse fhir output for notifications",
              e
            );
          }
        }

        // notify subscribers
        const subs = listeners.get(taskId);
        if (subs) {
          subs.forEach((cb) => {
            try {
              cb(null, fhir);
            } catch (e) {
              console.error("globalSyncPoller: listener error", e);
            }
          });
        }

        if (shouldRemove) {
          removeSyncTask(taskId);
        }
      } catch (err) {
        // store error info in cache for consumer diagnostics
        taskDataCache.set(taskId, { fetchedAt: Date.now(), error: err });
        // Do not emit error/failure notifications by design (user preference).
        const subs = listeners.get(taskId);
        if (subs) {
          subs.forEach((cb) => {
            try {
              cb(err, null);
            } catch (e) {
              console.error("globalSyncPoller: listener error", e);
            }
          });
        }
      }
    })
  );
}

function startPoller() {
  if (pollerTimer) return;
  pollerTimer = setInterval(() => {
    pollOnce().catch((e) => console.error("globalSyncPoller: poll error", e));
  }, POLL_INTERVAL);
  // run immediately once
  pollOnce().catch((e) =>
    console.error("globalSyncPoller: initial poll error", e)
  );
}

function stopPoller() {
  if (!pollerTimer) return;
  clearInterval(pollerTimer);
  pollerTimer = null;
}

export function initializeGlobalSyncPoller() {
  loadPersistedTasks();
  // Always attempt to discover in-progress tasks on startup
  discoverAndNotify().catch((e) =>
    console.warn("globalSyncPoller: discoverAndNotify failed", e)
  );
  if (activeTasks.size > 0) startPoller();
}

// Convenience helper: discover in-progress tasks and, if none are active,
// query for completed tasks to surface completed notifications (used on login/refresh).
export async function discoverAndNotify() {
  // first try to discover in-progress tasks
  try {
    await fetchInProgressTasks();
  } catch (e) {
    console.warn(
      "globalSyncPoller: fetchInProgressTasks failed during discoverAndNotify",
      e
    );
  }

  // if there are no active tasks, query completed tasks to emit completion notifications
  if (activeTasks.size === 0) {
    try {
      const completedQuery =
        "_tag=http://enliv.com/fhir/coding/task/tag/task-type|ehr-resource-sync&code=http://enliv.com/fhir/coding/task/code/process-code|long-running-operation&status=completed&_sort=-_lastUpdated";
      await fetchInProgressTasks(completedQuery);
    } catch (e) {
      console.warn(
        "globalSyncPoller: fetch completed tasks failed during discoverAndNotify",
        e
      );
    }
  }
}

// Discover running/in-progress FHIR Tasks and register them for polling
export async function fetchInProgressTasks(
  query = "_tag=http://enliv.com/fhir/coding/task/tag/task-type|ehr-resource-sync&code=http://enliv.com/fhir/coding/task/code/process-code|long-running-operation&status=in-progress",
  options = {}
) {
  try {
    // build URL using tenant from options or from token helper
    const tenant = options?.tenant || getTenantFromToken();
    const url = `fhir/tenants/${tenant}/search/Task${query ? "?" + query : ""}`;

    // Use the app HTTP client and let its interceptors populate Authorization and tenant headers.
    const res = await HTTPRequest.get(url);
    const data = res?.data || res;
    const entries = data?.entry || [];

    if (!entries || entries.length === 0) return [];

    const registered = [];

    entries.forEach((entry) => {
      try {
        const resource = entry?.resource;
        const taskId = resource?.id;
        if (!taskId) return;

        if (isTaskStale(resource)) {
          console.log(`globalSyncPoller: skipping stale task ${taskId}`);
          return;
        }

        // cache the resource as a raw FHIR response shape
        const fhirLike = { entry: [{ resource }] };
        // attempt to derive a friendly label and basic progress/status for consumers
        let derived = { fetchedAt: Date.now(), raw: fhirLike };
        try {
          const outputStr = resource?.output?.[0]?.valueString;
          if (outputStr) {
            const payload = JSON.parse(outputStr);
            derived.progress =
              typeof payload.progress === "number"
                ? Math.round(payload.progress)
                : 0;
            derived.status = payload.status || resource?.status || "RUNNING";
            derived.label =
              extractTaskGroupLabel(payload) ||
              resource?.description ||
              resource?.code ||
              null;
          }
        } catch (e) {
          /* ignore */
        }
        taskDataCache.set(taskId, derived);

        // register for active polling
        if (!activeTasks.has(taskId)) {
          activeTasks.add(taskId);
          registered.push(taskId);
        }

        // notify subscribers if exist
        const subs = listeners.get(taskId);
        if (subs)
          subs.forEach((cb) => {
            try {
              cb(null, fhirLike);
            } catch (e) {
              console.error(e);
            }
          });

        // attempt to extract progress and send initial notifications
        try {
          const outputStr = resource?.output?.[0]?.valueString;
          if (outputStr) {
            const payload = JSON.parse(outputStr);
            const prog =
              typeof payload.progress === "number" ? payload.progress : 0;
            const status = payload.status || resource?.status || "RUNNING";
            // On discovery, set last known progress so the poller can resume
            const last = lastProgressMap.get(taskId) || -1;
            if (typeof prog === "number" && prog !== last) {
              lastProgressMap.set(taskId, prog);
            }
            if (status === "COMPLETED" || prog === 100) {
              // emit a completion notification for discovered completed tasks (always)
              try {
                const metaLastUpdated = resource?.meta?.lastUpdated || null;
                const lastUpdatedTs = metaLastUpdated
                  ? Date.parse(metaLastUpdated)
                  : typeof payload.updatedAt === "number"
                  ? payload.updatedAt
                  : null;
                const notifId = `${taskId}-sync`;
                const taskGroupLabel = extractTaskGroupLabel(payload);
                store.dispatch(
                  generate_notification({
                    id: notifId,
                    message: `Data Sync Completed.`,
                    progress: 100,
                    status: "COMPLETED",
                    label: taskGroupLabel,
                    addedOn: lastUpdatedTs || Date.now(),
                    isRead: false,
                    type: "sync-complete",
                  })
                );
              } catch (e) {
                console.warn(
                  "globalSyncPoller: failed to emit completion notification for discovered completed task",
                  e
                );
              }
              // don't register completed tasks
              activeTasks.delete(taskId);
            }
            // also store parsed payload so consumers can read details like progressStats
            try {
              const existing = taskDataCache.get(taskId) || {
                fetchedAt: Date.now(),
                raw: fhirLike,
              };
              taskDataCache.set(taskId, {
                ...existing,
                fetchedAt: Date.now(),
                raw: fhirLike,
                parsed: payload,
                progressStats: payload.progressStats || null,
              });
            } catch (e) {
              /* ignore */
            }
          }
        } catch (e) {
          console.warn(
            "globalSyncPoller: failed to parse output while discovering tasks",
            e
          );
        }
      } catch (e) {
        console.error("globalSyncPoller: error handling discovered entry", e);
      }
    });

    persistActiveTasks();
    if (activeTasks.size > 0) startPoller();
    return registered;
  } catch (e) {
    console.error("globalSyncPoller: fetchInProgressTasks failed", e);
    throw e;
  }
}

export function addSyncTask(taskId, label) {
  if (!taskId) return;
  const id = String(taskId);
  activeTasks.add(id);
  // seed the cache with a minimal derived payload so UIs can show an initial label
  try {
    const existing = taskDataCache.get(id) || {
      fetchedAt: Date.now(),
      raw: null,
    };
    taskDataCache.set(id, {
      ...existing,
      label: label || existing.label || "Sync Task",
    });
  } catch (e) {
    /* ignore */
  }
  persistActiveTasks();
  startPoller();
}

export function removeSyncTask(taskId) {
  const id = String(taskId);
  activeTasks.delete(id);
  taskDataCache.delete(id);
  listeners.delete(id);
  lastProgressMap.delete(id);
  persistActiveTasks();
  if (activeTasks.size === 0) stopPoller();
}

export function subscribeToTask(taskId, cb) {
  if (!taskId || typeof cb !== "function") return () => {};
  const id = String(taskId);
  let subs = listeners.get(id);
  if (!subs) {
    subs = new Set();
    listeners.set(id, subs);
  }
  subs.add(cb);
  // return unsubscribe
  return () => {
    const s = listeners.get(id);
    if (s) {
      s.delete(cb);
      if (s.size === 0) listeners.delete(id);
    }
  };
}

export function getTaskData(taskId) {
  if (!taskId) return null;
  return taskDataCache.get(String(taskId)) || null;
}

export function listActiveTasks() {
  return Array.from(activeTasks);
}

export function isTaskActive(taskId) {
  return activeTasks.has(String(taskId));
}
